/**
 * SSO (DSP) backend URL rewriting. The Cognito path uses an app-origin relative
 * proxy; the SSO path must hit an absolute internal-proxy gateway that trusts the
 * DSP session cookie. toSsoUrl() rewrites relative get-user-profile-service proxy
 * URLs and leaves all other URLs unchanged.
 */

import { getSsoApiHost } from './config';
import { ePortalLog } from '@sgp/lib/util/log/Tools';
// Internal-proxy route for SSO. Non-prod uses the `mct` namespace; PROD uses `prod`.
const SSO_INTERNAL_ROUTE_NONPROD = 'ins-get-user-profile-service-mct-internal-proxy/v1';
const SSO_INTERNAL_ROUTE_BY_ENV = {
  SIT: SSO_INTERNAL_ROUTE_NONPROD,
  UAT: SSO_INTERNAL_ROUTE_NONPROD,
  PROD: 'ins-get-user-profile-service-prod-internal-proxy/v1',
};

/** Full SSO internal-proxy base for the current environment. */
const getSsoInternalBase = () => {
  const env = (typeof APP_ENV !== 'undefined' && APP_ENV) || 'SIT';
  const route = SSO_INTERNAL_ROUTE_BY_ENV[env] || SSO_INTERNAL_ROUTE_NONPROD;
  ePortalLog("[I]", `${getSsoApiHost()}/${route}`);
  return `${getSsoApiHost()}/${route}`;
};

// Matches the leading Cognito relative proxy prefix; only the endpoint suffix is kept.
const COGNITO_PROXY_PREFIX = /^\/ins-get-user-profile-service-[^/]+-proxy\/v\d+\//;

// The agent profile is read from the ODS agent service, which sits behind its own
// gateway route: a relative eB2B proxy for Cognito and an absolute internal proxy for SSO.
const COGNITO_AGENT_PROXY_PREFIX = /^\/ins-digilife-sg-agent-service-[^/]+-proxy\/v\d+\//;
const SSO_AGENT_INTERNAL_ROUTE_NONPROD = 'ins-digilife-sg-agent-service-cert05-internal-proxy/v1';
const SSO_AGENT_INTERNAL_ROUTE_BY_ENV = {
  SIT: SSO_AGENT_INTERNAL_ROUTE_NONPROD,
  UAT: SSO_AGENT_INTERNAL_ROUTE_NONPROD,
};

/** Full SSO internal-proxy base of the ODS agent service for the current environment. */
const getSsoAgentInternalBase = () => {
  const env = (typeof APP_ENV !== 'undefined' && APP_ENV) || 'SIT';
  const route = SSO_AGENT_INTERNAL_ROUTE_BY_ENV[env] || SSO_AGENT_INTERNAL_ROUTE_NONPROD;
  return `${getSsoApiHost()}/${route}`;
};

// Admin portal base for the encrypted dspi/dsps and the dspk, which are read before SSO login.
// The internal-proxy gateway requires an E2E trust token, so these calls go to the admin
// portal service host directly; only these two exact paths are requested from it.
const ADMIN_PORTAL_DSP_BASE_NONPROD =
  'https://ins-dis-admin-portal-app-1.hsbc-insh-sdpeapi-uat-mct.svc.apiapg02.shp.ape1.pre-prod.aws.cloud.hsbc';
const ADMIN_PORTAL_DSP_BASE_PROD =
  'https://ins-dis-admin-portal-app-1.hsbc-insh-sdpeapi-prod1.svc.apiapg02.shp.ape1.prod.aws.cloud.hsbc';
const DSP_PAIR_PATH = '/ins/api/v1/adm-portal/getDspiAndDsps';
const DSP_KEY_PATH = '/ins/api/v1/adm-portal/getDspk';

const getAdminPortalInternalBase = () => {
  const env = (typeof APP_ENV !== 'undefined' && APP_ENV) || 'SIT';
  return env === 'PROD' ? ADMIN_PORTAL_DSP_BASE_PROD : ADMIN_PORTAL_DSP_BASE_NONPROD;
};

/** Exact admin portal URL of the encrypted dspi/dsps for the current environment. */
export const getDspPairUrl = () => `${getAdminPortalInternalBase()}${DSP_PAIR_PATH}`;

/** Exact admin portal URL of the DSP dspk for the current environment. */
export const getDspKeyUrl = () => `${getAdminPortalInternalBase()}${DSP_KEY_PATH}`;

/** Rewrite a Cognito backend URL to its SSO internal-proxy equivalent, or return it unchanged. */
export const toSsoUrl = (url) => {
  ePortalLog("[I]", url);
  if (typeof url !== 'string') {
    return url;
  }
  if (COGNITO_AGENT_PROXY_PREFIX.test(url)) {
    return url.replace(COGNITO_AGENT_PROXY_PREFIX, `${getSsoAgentInternalBase()}/`);
  }
  if (!COGNITO_PROXY_PREFIX.test(url)) {
    return url;
  }
  return url.replace(COGNITO_PROXY_PREFIX, `${getSsoInternalBase()}/`);
};

export default toSsoUrl;
